import { router, Stack } from 'expo-router';
import * as Linking from 'expo-linking';
import * as React from 'react';
import { Alert, Platform, View } from 'react-native';

import { AccountSettingsScreen } from '@/components/profiles/account-settings-screen';
import { useSession } from '@/components/session-provider';
import {
  normalizeProfileLinks,
  profileLinksToText,
  updateMyProfile,
  type Profile,
} from '@/lib/member-profile';
import { supabase } from '@sixam/supabase/client';
import { authedFetch } from '@/lib/authed-fetch';

const PRIVACY_URL = 'https://6amrun.club/privacy';
const SUPPORT_URL = 'https://6amrun.club/support';

function confirmAccountDeletion(): Promise<boolean> {
  if (Platform.OS === 'web') {
    return Promise.resolve(
      Boolean(
        globalThis.confirm?.(
          'Permanently delete your 6am account and associated data? This cannot be undone.'
        )
      )
    );
  }

  return new Promise((resolve) => {
    Alert.alert(
      'Delete account?',
      'Your profile, memberships, registrations, contacts, connected services, and uploads will be permanently deleted. If you are the only owner of a club, ownership will be transferred to another club member when possible.',
      [
        { onPress: () => resolve(false), style: 'cancel', text: 'Keep account' },
        { onPress: () => resolve(true), style: 'destructive', text: 'Delete account' },
      ],
      { cancelable: true, onDismiss: () => resolve(false) }
    );
  });
}

function showAccountDeleted(): Promise<void> {
  if (Platform.OS === 'web') {
    globalThis.alert?.('Your account and associated data have been permanently deleted.');
    return Promise.resolve();
  }

  return new Promise((resolve) => {
    Alert.alert(
      'Account deleted',
      'Your account and associated data have been permanently deleted.',
      [{ onPress: () => resolve(), text: 'Done' }],
      { cancelable: false }
    );
  });
}

export default function AccountSettingsRoute() {
  const { session, profile } = useSession();
  const userId = session?.user.id;
  const userEmail = session?.user.email;

  const [savedProfile, setSavedProfile] = React.useState<Profile | null>(null);
  const [emailChange, setEmailChange] = React.useState('');
  const [phoneInput, setPhone] = React.useState<string | null>(null);
  const [phoneIsPublicInput, setPhoneIsPublic] = React.useState<boolean | null>(null);
  const [isSavingAccount, setIsSavingAccount] = React.useState(false);
  const [isDeletingAccount, setIsDeletingAccount] = React.useState(false);
  const [message, setMessage] = React.useState<string | null>(null);
  const [error, setError] = React.useState<string | null>(null);

  const currentProfile = savedProfile ?? profile;
  const phone = phoneInput ?? currentProfile?.phone ?? session?.user.phone ?? '';
  const phoneIsPublic = phoneIsPublicInput ?? currentProfile?.phone_is_public ?? false;
  const phoneVerified = Boolean(
    session?.user.phone_confirmed_at || currentProfile?.phone_verified_at
  );

  async function saveAccountChanges() {
    setIsSavingAccount(true);
    setError(null);
    setMessage(null);
    try {
      if (emailChange.trim()) {
        const { error: emailError } = await supabase.auth.updateUser({
          email: emailChange.trim(),
        });
        if (emailError) {
          throw emailError;
        }
      }

      if (phone.trim() && phone.trim() !== session?.user.phone) {
        const { error: phoneError } = await supabase.auth.updateUser({ phone: phone.trim() });
        if (phoneError) {
          throw phoneError;
        }
      }

      if (userId) {
        const next = await updateMyProfile(userId, {
          bio: currentProfile?.bio?.trim() || null,
          fullName: currentProfile?.full_name?.trim() || null,
          links: normalizeProfileLinks(profileLinksToText(currentProfile?.links ?? [])),
          phone: phone.trim() || null,
          phoneIsPublic,
        });
        setSavedProfile(next);
        setPhone(next.phone ?? '');
        setPhoneIsPublic(next.phone_is_public);
      }

      setMessage(
        emailChange.trim() || phone.trim() !== session?.user.phone
          ? 'Verification sent. Confirm the change from your email or phone.'
          : 'Account settings saved.'
      );
    } catch (accountError) {
      setError(
        accountError instanceof Error ? accountError.message : 'Could not save account settings.'
      );
    } finally {
      setIsSavingAccount(false);
    }
  }

  async function deleteAccount() {
    if (!(await confirmAccountDeletion())) return;

    setIsDeletingAccount(true);
    setError(null);
    setMessage(null);
    try {
      const response = await authedFetch('/api/me', { method: 'DELETE' });
      const payload = (await response.json().catch(() => null)) as { error?: string } | null;
      if (!response.ok) throw new Error(payload?.error ?? 'Could not delete this account.');

      await showAccountDeleted();
      await supabase.auth.signOut({ scope: 'local' });
      router.replace('/');
    } catch (deleteError) {
      setError(
        deleteError instanceof Error ? deleteError.message : 'Could not delete this account.'
      );
    } finally {
      setIsDeletingAccount(false);
    }
  }

  return (
    <View className="flex-1 bg-background">
      <Stack.Screen options={{ headerShown: process.env.EXPO_OS !== 'web', title: 'Account' }} />
      <Stack.Screen.BackButton displayMode="minimal">Me</Stack.Screen.BackButton>
      <AccountSettingsScreen
        emailChange={emailChange}
        error={error}
        isDeleting={isDeletingAccount}
        isSaving={isSavingAccount}
        message={message}
        onBack={() => router.back()}
        onEmailChange={setEmailChange}
        onDeleteAccount={() => void deleteAccount()}
        onPrivacy={() => void Linking.openURL(PRIVACY_URL)}
        onPhoneChange={setPhone}
        onPhoneIsPublicChange={setPhoneIsPublic}
        onSave={() => void saveAccountChanges()}
        onSupport={() => void Linking.openURL(SUPPORT_URL)}
        phone={phone}
        phoneIsPublic={phoneIsPublic}
        phoneVerified={phoneVerified}
        topPadding={24}
        userEmail={userEmail}
      />
    </View>
  );
}
